import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Grid2,
    Stack,
    Table,
    TableContainer,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import NotificationsIcon from '@mui/icons-material/Notifications';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import { TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';
import { formatCurrency } from '../../bookings/helpers/formatters';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';

const STATUS_CHIP_PROPS = {
    eligible: { label: 'upgrades.status.eligible', color: 'info' },
    sent: { label: 'upgrades.status.sent', color: 'warning' },
    settled: { label: 'upgrades.status.settled', color: 'success' },
    expired: { label: 'upgrades.status.expired', color: 'default' },
    declined: { label: 'upgrades.status.declined', color: 'default' },
};
const DEFAULT_CHIP_PROPS = { label: 'upgrades.status.eligible', color: 'default' };
const VA_MID = { verticalAlign: 'middle' };
const COLUMN_COUNT = 8;

function UpgradeOffersTable({
    rows,
    total,
    page,
    limit,
    setParams,
    loading,
    selectedIds,
    setSelectedIds,
    onSendOffer,
    onSendReminder,
    onDismiss,
    sendingOfferIds,
    sendingReminderIds,
    dismissingIds,
    sort,
    sortDir,
    onSort,
    hasActiveFilters = false,
}) {
    const { t } = useTranslation();
    const { showModal } = useModals();
    const navigate = useNavigate();
    const selectedIdSet = new Set(selectedIds);
    const eligibleRows = rows.filter((r) => r.status === 'eligible');
    const allEligibleSelected =
        eligibleRows.length > 0 && eligibleRows.every((r) => selectedIdSet.has(r.bookingId));
    const someEligibleSelected = eligibleRows.some((r) => selectedIdSet.has(r.bookingId));

    const toggleRow = (bookingId) => {
        setSelectedIds(
            selectedIdSet.has(bookingId)
                ? selectedIds.filter((id) => id !== bookingId)
                : [...selectedIds, bookingId],
        );
    };

    const togglePage = () => {
        if (allEligibleSelected) {
            const ids = new Set(eligibleRows.map((r) => r.bookingId));
            setSelectedIds(selectedIds.filter((id) => !ids.has(id)));
            return;
        }
        const next = new Set(selectedIds);
        eligibleRows.forEach((r) => next.add(r.bookingId));
        setSelectedIds([...next]);
    };

    const sortCell = (key, labelKey) => (
        <SortableTableCell arrow={sort === key ? sortDir : ''} onClick={() => onSort(key)}>
            {t(labelKey)}
        </SortableTableCell>
    );

    return (
        <Grid2 size={{ xs: 12 }}>
            <TableContainer sx={{ maxHeight: { xs: '60dvh', md: 'calc(100dvh - 256px)' } }}>
                <Table stickyHeader sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    size="small"
                                    checked={allEligibleSelected}
                                    indeterminate={!allEligibleSelected && someEligibleSelected}
                                    onChange={togglePage}
                                    disabled={eligibleRows.length === 0}
                                />
                            </TableCell>
                            {sortCell('name', 'upgrades.columns.guest')}
                            <TableCell>{t('upgrades.columns.group')}</TableCell>
                            <TableCell>{t('upgrades.columns.upgradeTo')}</TableCell>
                            {sortCell('price_difference', 'upgrades.columns.priceDifference')}
                            {sortCell('status', 'upgrades.columns.status')}
                            {sortCell('last_notified_at', 'upgrades.columns.activity')}
                            <TableCell align="right">{t('upgrades.columns.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable rows={Math.max(limit, 5)} cells={COLUMN_COUNT} />
                        ) : (
                            rows.map((row) => {
                                const isEligible = row.status === 'eligible';
                                const isSent = row.status === 'sent';
                                const isSendingOffer = sendingOfferIds.includes(row.bookingId);
                                const isSendingReminder = sendingReminderIds.includes(row.id);
                                const isDismissing = dismissingIds.includes(
                                    isEligible ? row.bookingId : row.id,
                                );
                                const chipProps =
                                    STATUS_CHIP_PROPS[row.status] || DEFAULT_CHIP_PROPS;
                                const dismissKey = isEligible
                                    ? 'upgrades.actions.dismissBookingConfirm'
                                    : 'upgrades.actions.dismissOfferConfirm';
                                const openDismiss = () =>
                                    showModal(MODAL_TYPE.CONFIRMATION, {
                                        title: t('confirmationModal.title'),
                                        content: t(dismissKey),
                                        confirm: () => onDismiss(row),
                                        maxWidth: 'xs',
                                        fullWidth: true,
                                    });

                                return (
                                    <TableRow
                                        key={`${row.bookingId}-${row.id || 'eligible'}`}
                                        hover
                                    >
                                        <TableCell padding="checkbox" sx={VA_MID}>
                                            <Checkbox
                                                size="small"
                                                checked={selectedIdSet.has(row.bookingId)}
                                                onChange={() => toggleRow(row.bookingId)}
                                                disabled={!isEligible}
                                            />
                                        </TableCell>
                                        <TableCell sx={VA_MID}>
                                            <Stack spacing={0.5}>
                                                <Typography
                                                    variant="body1"
                                                    sx={{ fontWeight: 700 }}
                                                >
                                                    {row.guestFirstName} {row.guestLastName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {row.guestEmail}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {row.guestPhone || t('upgrades.labels.noPhone')}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={VA_MID}>
                                            <Stack spacing={0.25}>
                                                {(row.currentComposition || []).map((line) => (
                                                    <Typography key={line} variant="body2">
                                                        {line}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={VA_MID}>
                                            <Stack spacing={0.25}>
                                                {(row.upgradeComposition || []).map((line) => (
                                                    <Typography key={line} variant="body2">
                                                        {line}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        </TableCell>
                                        <TableCell sx={VA_MID}>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {formatCurrency(row.priceDifference / 100)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={VA_MID}>
                                            <Chip
                                                size="small"
                                                label={t(chipProps.label)}
                                                color={chipProps.color}
                                                sx={{ minWidth: 90, justifyContent: 'center' }}
                                            />
                                        </TableCell>
                                        <TableCell sx={VA_MID}>
                                            {row.lastNotifiedAt ? (
                                                <Stack spacing={0.5}>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontWeight: 600 }}
                                                    >
                                                        {t('upgrades.activity.lastNotified', {
                                                            date: formatDateTimeInVenueTz(
                                                                row.lastNotifiedAt,
                                                            ),
                                                        })}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {t('upgrades.activity.sentTimes', {
                                                            count: row.notificationCount,
                                                        })}
                                                    </Typography>
                                                </Stack>
                                            ) : (
                                                <Chip
                                                    size="small"
                                                    label={t('upgrades.status.neverNotified')}
                                                    color="warning"
                                                    sx={{ minWidth: 130, justifyContent: 'center' }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="right" sx={VA_MID}>
                                            {(isEligible || isSent) && (
                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    justifyContent="flex-end"
                                                    alignItems="center"
                                                >
                                                    {isEligible && (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="primary"
                                                            startIcon={<SendIcon />}
                                                            disabled={isSendingOffer}
                                                            onClick={() =>
                                                                onSendOffer(row.bookingId)
                                                            }
                                                            sx={{ whiteSpace: 'nowrap' }}
                                                        >
                                                            {t('upgrades.actions.sendOffer')}
                                                        </Button>
                                                    )}
                                                    {(isEligible || isSent) && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="success"
                                                            startIcon={<CheckCircleOutlineIcon />}
                                                            onClick={() =>
                                                                navigate(
                                                                    `/bookings/edit/${row.bookingId}`,
                                                                    {
                                                                        state: {
                                                                            upgradeComposition:
                                                                                row.offerComposition,
                                                                        },
                                                                    },
                                                                )
                                                            }
                                                            sx={{ whiteSpace: 'nowrap' }}
                                                        >
                                                            {t('upgrades.actions.applyUpgrade')}
                                                        </Button>
                                                    )}
                                                    {isSent && (
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            startIcon={<NotificationsIcon />}
                                                            disabled={isSendingReminder}
                                                            onClick={() => onSendReminder(row.id)}
                                                            sx={{ whiteSpace: 'nowrap' }}
                                                        >
                                                            {t('upgrades.actions.sendReminder')}
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<BlockIcon />}
                                                        disabled={isDismissing}
                                                        onClick={openDismiss}
                                                        sx={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        {t('upgrades.actions.dismiss')}
                                                    </Button>
                                                </Stack>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={COLUMN_COUNT}>
                                    <Box sx={{ py: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t(
                                                hasActiveFilters
                                                    ? 'upgrades.emptyFiltered'
                                                    : 'upgrades.empty',
                                            )}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={rows.length <= 0 ? 0 : page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                SelectProps={{ size: 'small' }}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Grid2>
    );
}

export default UpgradeOffersTable;
